import { useMemo, useState } from 'react';
import { Collections_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/email-template/v1/emailtemplate.pb';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import {
  DynamicFieldKey_Enum as DynamicFieldKeys,
  TemplateType_Slug,
} from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { EmailTemplateQueries } from '@frontend/api-email-template';
import { generateThumbnail } from '@frontend/api-html-thumbnail-generator';
import { EmailComposer, OnSaveChanges, OnTemplateNameChange } from '@frontend/content-composer';
import { useDynamicFields, useFormatDynamicFieldAndLabel } from '@frontend/dynamic-fields-hooks';
import { useEmailTemplates } from '@frontend/email-templates';
import { useTranslation } from '@frontend/i18n';
import { useMiniChatBottomStyling } from '@frontend/mini-chat-store';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { BulkEmailPrefixes } from '@frontend/tracking-prefixes';
import { useAlert } from '@frontend/design-system';
import { useBulkMessageEditorContext } from '../../bulk-message-editor.context';
import { NEW_CAMPAIGN_ID } from '../../constants';
import { useBulkNavigator } from '../../hooks/use-bulk-navigator';
import { useBulkUrlData } from '../../hooks/use-bulk-url-data';

const hiddenSubjectDynamicFields = [
  DynamicFieldKeys.BUSINESS_GROUP_ADDRESS,
  DynamicFieldKeys.BUSINESS_GROUP_EMAIL,
  DynamicFieldKeys.ONLINE_BILL_PAY_LINK,
  DynamicFieldKeys.PREFERENCE_MANAGEMENT_LINK,
  DynamicFieldKeys.SCHEDULE_LINK,
  DynamicFieldKeys.UNKNOWN,
  DynamicFieldKeys.UNSUBSCRIBE_LINK,
];

const hiddenTextDynamicFields = [
  DynamicFieldKeys.PREFERENCE_MANAGEMENT_LINK,
  DynamicFieldKeys.UNKNOWN,
  DynamicFieldKeys.UNSUBSCRIBE_LINK,
];

// Custom height offset to account for headers, footers, and other UI elements
const HEIGHT_OFFSET = 73;

export const BulkEmailComposer = () => {
  const alert = useAlert();
  const { t } = useTranslation('bulk-messaging');

  const { convertDynamicFieldToLabel } = useFormatDynamicFieldAndLabel();
  const {
    locationIds,
    setMessageTemplate,
    setSubject,
    setTemplateId,
    setTemplateJson,
    setThumbnailMediaId,
    subject: savedSubject,
    templateJson: savedTemplateJson,
  } = useBulkMessageEditorContext();
  const { campaignId, isSystemTemplate, startFresh, templateId: urlTemplateId } = useBulkUrlData();
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);

  const { data: customTemplate, isLoading: isCustomTemplateLoading } = EmailTemplateQueries.useGetEmailTemplate(
    { templateId: urlTemplateId || '' },
    {
      enabled: !!urlTemplateId && urlTemplateId !== NEW_CAMPAIGN_ID && !isSystemTemplate,
      onError: () => {
        alert.error(t('There was an error fetching the template.'));
      },
    }
  );

  const { data: systemTemplate, isLoading: isSystemTemplateLoading } = EmailTemplateQueries.useGetSystemEmailTemplate(
    { templateId: urlTemplateId || '' },
    {
      enabled: !!urlTemplateId && urlTemplateId !== NEW_CAMPAIGN_ID && isSystemTemplate,
      onError: () => {
        alert.error(t('There was an error fetching the template.'));
      },
    }
  );

  const template = isSystemTemplate ? systemTemplate : customTemplate;
  const isLoading = isSystemTemplate ? isSystemTemplateLoading : isCustomTemplateLoading;
  const hasLoadedTemplate = !!urlTemplateId && !!template && !isLoading;

  const navigateTo = useBulkNavigator(CampaignType.EMAIL);
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isNewComposeEmailEnabled = getFeatureFlagValue('bulk-messaging-compose-email-revamp');

  const onBack = () => {
    if (urlTemplateId && isNewComposeEmailEnabled) {
      navigateTo.new();
    } else if (urlTemplateId) {
      navigateTo.templates();
    } else if (savedTemplateJson && !startFresh) {
      const isNewCampaign = !campaignId || campaignId === NEW_CAMPAIGN_ID;
      if (isNewComposeEmailEnabled && isNewCampaign) navigateTo.new();
      else navigateTo.editor();
    } else {
      navigateTo.new();
    }
  };

  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const { createTemplate, updateTemplate, updateTemplateLocations } = useEmailTemplates();
  const miniChatHeightOffset = useMiniChatBottomStyling();

  // APPLY CHANGES LOGIC
  const handleTemplateLocations = () => {
    const sortLocationIds = (locationIds: string[]) =>
      JSON.stringify([...locationIds].sort((a, b) => a.localeCompare(b)));
    const locationIdsHasChanged = sortLocationIds(locationIds) !== sortLocationIds(selectedLocationIds);

    if (locationIdsHasChanged) {
      updateTemplateLocations({
        templateId: urlTemplateId,
        locationIds: selectedLocationIds,
      });
    }
  };

  const onApplyChanges = (changes: OnSaveChanges) => {
    updateTemplate(changes);
    handleTemplateLocations();
  };

  // SAVE LOGIC
  const onSave = async (changes: OnSaveChanges, applyChanges: boolean) => {
    setSubject(changes.subject);
    setMessageTemplate(changes.templateHtml);
    setTemplateJson(changes.templateJson);
    if (urlTemplateId && urlTemplateId !== NEW_CAMPAIGN_ID) {
      setTemplateId(urlTemplateId);
    }
    const thumbnail = await generateThumbnail(changes.templateHtml);
    if (thumbnail) setThumbnailMediaId(thumbnail);

    if (applyChanges) {
      onApplyChanges(changes);
    }

    navigateTo.editor();
  };

  // TEMPLATE NAME SAVE LOGIC
  const onTemplateNameSave = async (data: OnTemplateNameChange) => {
    setIsUpdatingTemplate(true);
    await createTemplate({ ...data, collection: Collections_Enum.BULK_MESSAGING });
    setIsUpdatingTemplate(false);
  };

  // DYNAMIC FIELDS LOGIC
  const templateTypeSlug = TemplateType_Slug.BULK_MESSAGE;
  const { dynamicFields } = useDynamicFields([templateTypeSlug]);
  const subjectDynamicFields = dynamicFields.filter(
    (field) => field.val && !hiddenSubjectDynamicFields.includes(field.val)
  );
  const textDynamicFields = dynamicFields.filter((field) => field.val && !hiddenTextDynamicFields.includes(field.val));

  // SUBJECT
  const subject = useMemo(() => {
    if (hasLoadedTemplate) {
      return convertDynamicFieldToLabel(template.subject) ?? ''; // TODO: Change convertDynamicFieldToLabel to fallback to ''
    }
    if (startFresh) {
      return '';
    }
    return convertDynamicFieldToLabel(savedSubject) ?? '';
  }, [convertDynamicFieldToLabel, hasLoadedTemplate, savedSubject, startFresh, template?.subject]);

  // TEMPLATE JSON
  const templateJson = useMemo(() => {
    if (hasLoadedTemplate) {
      return template.templateJson ?? '';
    }
    if (startFresh) {
      return '';
    }
    return savedTemplateJson;
  }, [hasLoadedTemplate, savedTemplateJson, startFresh, template?.templateJson]);

  return (
    <EmailComposer
      composerMode='marketing'
      css={{
        height: `calc(100vh - ${HEIGHT_OFFSET}px)`,
        paddingBottom: `${miniChatHeightOffset}px`,
        transition: 'padding-bottom 300ms ease-in-out',
      }}
      createTemplateAccess
      disabledBack={isUpdatingTemplate}
      editTemplateAccess={!!(template?.orgId && template?.orgId === selectedOrgId)}
      isLoading={isLoading}
      locationIds={locationIds}
      onBack={onBack}
      onSave={onSave}
      onTemplateNameSave={onTemplateNameSave}
      subject={subject}
      subjectDynamicFields={subjectDynamicFields}
      templateJson={templateJson}
      templateTitle={template?.title}
      templateTypeSlug={templateTypeSlug}
      textDynamicFields={textDynamicFields}
      trackingPrefix={BulkEmailPrefixes.Composer}
    />
  );
};
